import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";
import { getUserId } from "../utils/getUserId";

/* ================= JWT decode (claim varsa okumak için) ================= */
function decodeJwtPayload(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(payload)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getUyelikTipFromStorageOrToken() {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  if (token) {
    const payload = decodeJwtPayload(token);
    const tip =
      payload?.uyelikTip ||
      payload?.uyelik_type ||
      payload?.membershipType ||
      null;
    if (tip) return String(tip);
  }

  const tipLocal = localStorage.getItem("uyelikTip");
  const tipSession = sessionStorage.getItem("uyelikTip");
  return tipLocal || tipSession || null;
}

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [yetkiler, setYetkiler] = useState(null); // null=loading

  /**
   * ✅ KRİTİK: localStorage değişince React render olmaz.
   * Bu nedenle authVersion ile "auth değişti" sinyalini state'e çeviriyoruz.
   */
  const [authVersion, setAuthVersion] = useState(0);

  useEffect(() => {
    const bump = () => setAuthVersion((v) => v + 1);

    // aynı sekmede login/logout için: custom event
    window.addEventListener("auth-changed", bump);

    // farklı sekmede değişim için: native storage event
    window.addEventListener("storage", bump);

    return () => {
      window.removeEventListener("auth-changed", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  // authVersion değiştikçe token/userId yeniden okunur
  const userId = useMemo(() => getUserId(), [authVersion]);
  const token = useMemo(
    () => localStorage.getItem("token") || sessionStorage.getItem("token"),
    [authVersion]
  );

  const uyelikTip = useMemo(() => getUyelikTipFromStorageOrToken(), [token, authVersion]);

  const hasRoleBypass = useMemo(() => {
    return uyelikTip === "KUYUMCU" || uyelikTip === "SUPER_USER";
  }, [uyelikTip]);

  const yetkiSet = useMemo(() => new Set(yetkiler || []), [yetkiler]);

  const can = useCallback(
    (code) => {
      if (hasRoleBypass) return true;
      if (yetkiSet.has("ALL")) return true;
      return yetkiSet.has(code);
    },
    [hasRoleBypass, yetkiSet]
  );

  const refresh = useCallback(async () => {
    // login yoksa => boş
    if (!userId || !token) {
      setYetkiler([]);
      return;
    }

    // KUYUMCU / SUPER_USER => ALL
    if (hasRoleBypass) {
      setYetkiler(["ALL"]);
      return;
    }

    try {
      const resp = await axiosInstance.get("/me/yetki-kodlari", {
        params: { girisYapanUserId: userId },
      });
      setYetkiler(resp.data || []);
    } catch (e) {
      console.error("Yetkiler alınamadı:", e);
      setYetkiler([]);
    }
  }, [userId, token, hasRoleBypass]);

  // ✅ KRİTİK: kullanıcı/token değişince eski state'i sıfırla
  useEffect(() => {
    setYetkiler(null); // loading'e çek (eski yetkilerle karar verilmesin)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, token]);

  // ✅ KRİTİK: her değişimde yeniden yükle
  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      yetkiler,
      isLoading: yetkiler === null,
      can,
      refresh,
      uyelikTip,
      hasRoleBypass,
      clearPermissions: () => setYetkiler([]),

      // dışarıdan çağırmak istersen (logout vs.) authVersion tetiklemek için:
      bumpAuth: () => setAuthVersion((v) => v + 1),
    }),
    [yetkiler, can, refresh, uyelikTip, hasRoleBypass]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermission() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermission must be used within PermissionProvider");
  }
  return ctx;
}
